// /disputes/$id — dispute detail + adjudication (SM-291 / SM-295).
//
// The decision made on this page is the most consequential single click
// in the backoffice: it finalizes a market for EVERY holder, releases
// payouts computed from whichever winner the admin picks, and moves the
// disputer's stake either back to them or to treasury. It cannot be
// undone.
//
// The page is therefore built around making the operator certain before
// they commit:
//
//   * the blast radius is stated as a number ("settles 412 positions
//     across 208 users"), not as a warning adjective;
//   * REJECT and ACCEPT are separate, explicitly-labelled paths with the
//     stake consequence spelled out on each;
//   * a corrected winner must be picked before Accept is available;
//   * the confirmation restates what will happen in plain language;
//   * the dispute's last-known updated_at rides along as an
//     optimistic-concurrency token, so an admin acting on a stale tab
//     gets a conflict instead of overwriting a colleague's decision.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { SparkAmount } from "@/components/ui/SparkAmount";
import { Textarea } from "@/components/ui/Textarea";
import { useMyPerms } from "@/hooks/useMyPerms";
import {
  disputeEventLabel,
  disputeMarketLabel,
  disputeStatusLabel,
  disputeTone,
  disputeUserLabel,
  formatDuration,
  isDecidable,
  untilMs,
  useAdjudicate,
  useDispute,
  type DisputeDecision,
} from "@/lib/dispute-queries";
import { humanizeError } from "@/lib/errorMessages";
import { fmtDate, fmtRelative } from "@/lib/format";
import { Perm } from "@/lib/perms";
import { toast } from "@/lib/toast";
import { useAdminMap, adminName } from "@/hooks/useAdminNames";
import { useEventNames, useMarketNames } from "@/hooks/useCatalogNames";
import { useUserNames, userName } from "@/hooks/useUserNames";

export const Route = createFileRoute("/_app/disputes/$id")({
  component: DisputeDetailPage,
});

function DisputeDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const perms = useMyPerms();
  const admins = useAdminMap();

  const q = useDispute(id);
  const adjudicate = useAdjudicate(id);

  const [decision, setDecision] = useState<DisputeDecision | null>(null);
  const [correctedOutcome, setCorrectedOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Name resolution, hoisted above the loading/error returns so the hook
  // order stays fixed across renders. Each list is empty until the
  // dispute arrives, and only ids the server left UNNAMED are looked up
  // — a healthy response spends nothing here (SM-349).
  const row = q.data?.dispute;
  const resolveCatalog = perms.loaded && perms.has(Perm.CatalogViewAll);
  const unnamedMarketIDs = useMemo(
    () => (row && !row.market_question ? [row.market_id] : []),
    [row],
  );
  const unnamedEventIDs = useMemo(
    () => (row && !row.event_title && row.event_id ? [row.event_id] : []),
    [row],
  );
  // The filer, plus every USER actor on the timeline. Admin actors are
  // resolved from the local admin directory instead — looking one up in
  // client-service would only ever miss.
  const unnamedUserIDs = useMemo(() => {
    const out = new Set<string>();
    if (row?.user_id && !row.user_nickname && !row.user_email) out.add(row.user_id);
    for (const e of q.data?.timeline ?? []) {
      if (e.actor_kind === "USER" && e.actor_id && !e.actor_name) out.add(e.actor_id);
    }
    return [...out];
  }, [row, q.data?.timeline]);
  const marketNames = useMarketNames(unnamedMarketIDs, undefined, resolveCatalog);
  const eventNames = useEventNames(unnamedEventIDs, resolveCatalog);
  const userNames = useUserNames(unnamedUserIDs, perms.has(Perm.UserView));

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <ErrorPanel error={q.error} reset={() => void q.refetch()} />;
  }

  const { dispute: d, timeline, outcomes, affected_positions, affected_users } = q.data;
  const canReview = perms.has(Perm.DisputeReview);
  const decidable = isDecidable(d);
  const windowLeft = untilMs(d.window_ends_at_ms);

  // ACCEPT_CORRECT is the only decision that needs an extra input, and
  // it must not be a no-op: picking the outcome that already won would
  // "correct" the market to itself.
  const correctedIsCurrent = correctedOutcome === d.provisional_outcome_id;
  const canSubmit =
    canReview &&
    decidable &&
    decision !== null &&
    (decision !== "ACCEPT_CORRECT" || (correctedOutcome !== "" && !correctedIsCurrent));

  async function commit() {
    if (!decision) return;
    try {
      await adjudicate.mutateAsync({
        decision,
        corrected_outcome_id: decision === "ACCEPT_CORRECT" ? correctedOutcome : undefined,
        notes: notes.trim() || undefined,
        // The optimistic-concurrency token: the dispute exactly as this
        // tab last saw it. If a colleague decided in the meantime the
        // server answers with a conflict instead of letting this
        // decision overwrite theirs on an already-finalized market.
        expected_updated_at_ms: d.updated_at_ms || undefined,
      });
      toast.success(decisionSummary(decision));
      setConfirming(false);
      void navigate({ to: "/disputes" });
    } catch {
      // Rendered in the modal — the operator needs to read a conflict,
      // not have it flash past in a toast.
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/disputes" className="text-2xs text-muted hover:text-fg">
            ← Disputes
          </Link>
          {/* The contested MARKET is the title — it is what this page
              decides about. The event is the line above it, so a
              multi-market event reads "which contest, then which leg"
              rather than leaving the operator to infer it from a ULID. */}
          {disputeEventLabel(d, eventNames) && (
            <p className="mt-1 truncate text-xs text-muted">
              {disputeEventLabel(d, eventNames)}
            </p>
          )}
          <h1 className="mt-0.5 font-display text-2xl tracking-tight">
            {disputeMarketLabel(d, marketNames)}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-2xs text-muted">
            <Badge tone={disputeTone(d)}>{disputeStatusLabel(d)}</Badge>
            <span>{d.dispute_id}</span>
            <span>·</span>
            <span>filed {fmtRelative(new Date(d.created_at_ms).toISOString())}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          {/* The disputer's case. */}
          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>The claim</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <Row label="Filed by" value={disputeUserLabel(d, userNames)} />
                <Row label="Market" value={disputeMarketLabel(d, marketNames)} />
                {disputeEventLabel(d, eventNames) && (
                  <Row label="Event" value={disputeEventLabel(d, eventNames)} />
                )}
                <Row
                  label="Stake"
                  value={<SparkAmount value={d.stake_amount} className="tabular-nums" />}
                />
                <Row
                  label="Contesting"
                  value={d.provisional_outcome_name || d.provisional_outcome_id || "—"}
                />
                <Row
                  label="Stake status"
                  value={stakeLabel(d.stake_settlement)}
                />
              </dl>
              <div>
                <p className="mb-1 text-2xs uppercase tracking-wide text-muted">Evidence</p>
                <p className="whitespace-pre-wrap rounded-lg bg-surface-2 p-3 text-sm text-fg">
                  {d.evidence || "—"}
                </p>
              </div>
            </CardBody>
          </Card>

          {/* Adjudication. */}
          {d.status === "ACCEPTED" || d.status === "REJECTED" ? (
            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Decision</CardTitle>
              </CardHeader>
              <CardBody className="space-y-2 text-sm">
                <Row label="Outcome" value={decisionSummary(d.decision as DisputeDecision)} />
                <Row label="Decided by" value={adminName(admins, d.decided_by)} />
                <Row
                  label="Decided at"
                  value={d.decided_at_ms ? fmtDate(new Date(d.decided_at_ms).toISOString()) : "—"}
                />
                {d.corrected_outcome_name && (
                  <Row label="Corrected winner" value={d.corrected_outcome_name} />
                )}
                {d.decision_notes && <Row label="Notes" value={d.decision_notes} />}
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Decide</CardTitle>
              </CardHeader>
              <CardBody className="space-y-4">
                {!canReview && (
                  <p className="rounded-lg bg-surface-2 px-3 py-2 text-sm text-muted">
                    You need the DISPUTE_REVIEW permission to decide this dispute.
                  </p>
                )}
                {/* Informational only (SM-295) — the countdown never gates the
                    controls below. What it does change is the consequence, so
                    it says so: finalizing now closes the window for everyone,
                    including holders who have not filed yet. */}
                {canReview && decidable && windowLeft !== null && (
                  <p className="rounded-lg bg-surface-2 px-3 py-2 text-sm text-muted">
                    The dispute window is still open — {formatDuration(windowLeft)} left.
                    You can decide now; doing so closes the window, so any holder who
                    has not disputed yet no longer can.
                  </p>
                )}
                {canReview && !decidable && (
                  <p className="rounded-lg bg-surface-2 px-3 py-2 text-sm text-muted">
                    This dispute is {disputeStatusLabel(d).toLowerCase()} and can no longer
                    be decided.
                  </p>
                )}

                <fieldset disabled={!canReview || !decidable} className="space-y-2">
                  <DecisionOption
                    id="REJECT"
                    selected={decision === "REJECT"}
                    onSelect={setDecision}
                    title="Reject — the result stands"
                    body="The provisional winner is confirmed and the market finalizes unchanged. The disputer's stake is forfeited to treasury."
                  />
                  <DecisionOption
                    id="ACCEPT_CORRECT"
                    selected={decision === "ACCEPT_CORRECT"}
                    onSelect={setDecision}
                    title="Accept — correct the winner"
                    body="A different outcome wins. Every position on this market re-settles against the corrected winner, and the stake is refunded in full."
                  >
                    <div className="mt-2">
                      <Select
                        value={correctedOutcome}
                        onChange={(e) => setCorrectedOutcome(e.target.value)}
                        placeholder="Select the correct winner…"
                        // The current winner is filtered OUT rather than
                        // shown disabled: "correcting" a market to the
                        // outcome that already won is a no-op, and
                        // offering it invites the click.
                        options={outcomes
                          .filter((o) => !o.is_current)
                          .map((o) => ({ value: o.outcome_id, label: o.name }))}
                      />
                    </div>
                  </DecisionOption>
                  <DecisionOption
                    id="ACCEPT_VOID"
                    selected={decision === "ACCEPT_VOID"}
                    onSelect={setDecision}
                    title="Accept — void the market"
                    body="No outcome wins. Every holder is refunded their cost basis and the stake is refunded in full."
                  />
                </fieldset>

                <div>
                  <label htmlFor="decision-notes" className="mb-1 block text-2xs uppercase tracking-wide text-muted">
                    Notes (visible to the disputer)
                  </label>
                  <Textarea
                    id="decision-notes"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Explain the decision — this is what the disputer sees."
                    disabled={!canReview || !decidable}
                  />
                </div>

                <Button disabled={!canSubmit} onClick={() => setConfirming(true)}>
                  Apply decision
                </Button>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Context rail: blast radius + timeline. */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>Impact</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm">
              {/* Naming the market here is not decoration: these counts
                  are scoped to it alone, and on a multi-market event an
                  operator would otherwise read them as the whole
                  event's exposure. */}
              <p className="text-muted">
                A decision here settles{" "}
                <span className="font-medium text-fg">
                  {disputeMarketLabel(d, marketNames)}
                </span>
                :
              </p>
              <p className="text-2xl tabular-nums text-fg">
                {affected_positions.toLocaleString()}{" "}
                <span className="text-sm text-muted">positions</span>
              </p>
              <p className="text-2xl tabular-nums text-fg">
                {affected_users.toLocaleString()}{" "}
                <span className="text-sm text-muted">users</span>
              </p>
              <p className="text-2xs text-muted">
                Including everyone who did not dispute. This cannot be undone.
              </p>
              {disputeEventLabel(d, eventNames) && (
                <p className="text-2xs text-muted">
                  Other markets in {disputeEventLabel(d, eventNames)} are not
                  affected.
                </p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <ol className="divide-y divide-border">
                {timeline.map((e, i) => (
                  <li key={i} className="px-4 py-2.5">
                    <p className="text-sm text-fg">{e.message || e.kind}</p>
                    <p className="mt-0.5 text-2xs text-muted">
                      {e.actor_kind ?? "SYSTEM"}
                      {e.actor_id ? ` · ${timelineActorLabel(e, admins, userNames)}` : ""} ·{" "}
                      {fmtRelative(new Date(e.occurred_at_ms).toISOString())}
                    </p>
                  </li>
                ))}
                {timeline.length === 0 && (
                  <li className="px-4 py-3 text-sm text-muted">No events yet.</li>
                )}
              </ol>
            </CardBody>
          </Card>
        </div>
      </div>

      <ConfirmModal
        open={confirming}
        tone={decision === "REJECT" ? "primary" : "danger"}
        title="Finalize this market?"
        description={
          <div className="space-y-2 text-sm">
            <p>{decision ? decisionSummary(decision) : ""}</p>
            <p className="text-muted">
              This settles {affected_positions.toLocaleString()} positions across{" "}
              {affected_users.toLocaleString()} users — including everyone who did not
              dispute — and cannot be undone.
            </p>
          </div>
        }
        confirmLabel="Finalize"
        loading={adjudicate.isPending}
        error={adjudicate.error ? humanizeError(adjudicate.error) : null}
        onConfirm={() => void commit()}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

/**
 * Who did a timeline entry, as a name.
 *
 * Three sources, because a dispute timeline mixes actor kinds: the
 * server-supplied `actor_name` (which backoffice-service fills for USER
 * actors, the one kind this app has no directory for), the local admin
 * directory, and finally the client-user map. Falls through to the raw
 * id — an unnameable actor still has to be attributable (SM-349 AC 7).
 */
function timelineActorLabel(
  e: { actor_id?: string; actor_kind?: string; actor_name?: string },
  admins: Map<string, string>,
  users: Map<string, { display_name?: string; email?: string }>,
): string {
  if (e.actor_name) return e.actor_name;
  if (!e.actor_id) return "—";
  if (e.actor_kind === "USER") return userName(users, e.actor_id);
  return adminName(admins, e.actor_id);
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-2xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5 text-fg">{value}</dd>
    </div>
  );
}

function DecisionOption({
  id,
  selected,
  onSelect,
  title,
  body,
  children,
}: {
  id: DisputeDecision;
  selected: boolean;
  onSelect: (d: DisputeDecision) => void;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <label
      className={[
        "block cursor-pointer rounded-lg border p-3 transition-colors",
        selected ? "border-primary bg-primary/5" : "border-border hover:bg-surface-2",
      ].join(" ")}
    >
      <div className="flex items-start gap-2.5">
        <input
          type="radio"
          name="dispute-decision"
          checked={selected}
          onChange={() => onSelect(id)}
          className="mt-1"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-fg">{title}</p>
          <p className="mt-0.5 text-2xs text-muted">{body}</p>
          {selected ? children : null}
        </div>
      </div>
    </label>
  );
}

/** One-line restatement of a decision, reused in the confirm + the log. */
function decisionSummary(decision: DisputeDecision | undefined): string {
  switch (decision) {
    case "REJECT":
      return "The original result stands. The stake is forfeited to treasury.";
    case "ACCEPT_CORRECT":
      return "The winner is corrected and every position re-settles. The stake is refunded.";
    case "ACCEPT_VOID":
      return "The market is voided and every holder is refunded their cost basis. The stake is refunded.";
    default:
      return "";
  }
}

function stakeLabel(s: string | undefined): string {
  switch (s) {
    case "HELD":
      return "Held in escrow";
    case "REFUNDED":
      return "Refunded to the disputer";
    case "FORFEITED":
      return "Forfeited to treasury";
    case "FAILED":
      return "Collection failed";
    default:
      return "—";
  }
}
